import { Centrifuge, ErrorContext, State } from "centrifuge";
import React from "react";

type RealtimeContextProviderProps = {
  endpoint: string;
  debug?: boolean;
  lazy?: boolean;
  children: React.ReactNode;
  token?: string;
  getToken?: () => Promise<string>;
  onError?: (err: ErrorContext) => void;
};

const RealtimeContext = React.createContext<RealtimeContextShape | undefined>(
  undefined,
);

// context.ts
type RealtimeContextShape = {
  connection: Centrifuge | undefined;
  connectionState: State;
};

function RealtimeContextProvider(props: RealtimeContextProviderProps) {
  const [connection, setConnection] =
    React.useState<RealtimeContextShape["connection"]>(undefined);
  const [connectionState, setConnectionState] = React.useState<State>(
    State.Disconnected,
  );

  React.useEffect(() => {
    if (connection === undefined) {
      console.log("[realtime-context-provider] creating new instance");
      const centrifuge = new Centrifuge(props.endpoint, {
        debug: props.debug,
        token: props.token ?? "",
        getToken: props.getToken,
      });

      centrifuge.on("error", (err) => {
        console.error("[realtime-context-provider] connection error: ", err);
        props.onError?.(err);
      });

      centrifuge.on("state", (state) => {
        console.info(
          "[realtime-context-provider] state changed: ",
          state.newState,
        );
        setConnectionState(state.newState);
      });

      setConnection(centrifuge);
    }

    return () => {
      if (connection !== undefined) {
        console.log("[realtime-context-provider] disconnecting");
        connection.disconnect();
        connection.removeAllListeners();
        setConnection(undefined);
        setConnectionState(State.Disconnected);
      }
    };
  }, []);

  React.useEffect(() => {
    if (connection && !props.lazy) {
      connection.connect();
    }
  }, [props.lazy, connection]);

  return (
    <RealtimeContext.Provider value={{ connection, connectionState }}>
      {props.children}
    </RealtimeContext.Provider>
  );
}

function useRealtimeContext() {
  const context = React.useContext(RealtimeContext);
  if (context === undefined) {
    throw new Error(
      "useRealtimeContext must be used inside RealtimeContextProvider",
    );
  }

  return context;
}

export { RealtimeContextProvider, useRealtimeContext };
